import React, { FC, useState } from 'react';
import ConfirmDialog from '@components/Dialogs/ConfirmDialog';
import useStore from '@components/MainStage/store';
import { Modes } from '@constants/canvas/layers';
import { useToast } from '@context/Toast/ToastProvider';
import useAssets from '@hooks/useAssets';
import useProjectId from '@hooks/useProjectId';
import useSaveEntitiesResponse from '@hooks/useSaveEntitiesResponse';
import { Box } from '@mui/material';
import { useUpdateNodesActionMutation } from '@store/services/nodes';
import {
  INodeActionOutcome,
  NodeActionResult,
  NodeActionType,
  NodeDTO,
} from '@store/services/nodes/types';
import { useGetProjectDomainsQuery } from '@store/services/projects';
import { NODE_ACTIONS } from '@utils/nodeActions';

import { AddScopeTree } from './AddScopeTree';
import { NodeActionConfirmModal } from './NodeActionConfirmModal';

type Props = {
  open: boolean;
  onClose: () => void;
  nodeIds: string[];
  mode: Modes;
};

export const AddScopeModal: FC<Props> = ({ open, onClose, nodeIds, mode }) => {
  const { getNodeById } = useStore();
  const projectId = useProjectId();
  const { data: domains = [] } = useGetProjectDomainsQuery(projectId, {
    skip: !projectId,
  });
  const { selectedAssetId } = useAssets();
  const { showToast } = useToast();
  const { saveResponseToRedux } = useSaveEntitiesResponse();
  const [updateNodesAction, { isLoading }] = useUpdateNodesActionMutation();
  const [selected, setSelected] = useState<string[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [outcomes, setOutcomes] = useState<INodeActionOutcome[]>([]);
  const config = NODE_ACTIONS[NodeActionType.AddTargetScope]!;

  const toItem = (outcome: INodeActionOutcome) => ({
    id: outcome.node_id,
    name:
      (getNodeById(outcome.node_id)?.data?.dto as NodeDTO | undefined)?.name ??
      outcome.node_id,
  });

  const close = () => {
    setConfirming(false);
    setSelected([]);
    setOutcomes([]);
    onClose();
  };

  const run = async (force: boolean) => {
    const response = await updateNodesAction({
      nodeIds,
      actionType: NodeActionType.AddTargetScope,
      payload: { domainIds: selected },
      mode,
      assetId: selectedAssetId || undefined,
      force,
    }).unwrap();

    if (!response.applied) {
      setOutcomes(response.outcomes);
      setConfirming(true);
      return;
    }
    saveResponseToRedux(response);
    showToast(config.successMessage, 'success');
    setConfirming(false);
    setSelected([]);
    setOutcomes([]);
  };

  return (
    <>
      <ConfirmDialog
        open={open && !confirming}
        onClose={close}
        onBack={close}
        onConfirm={() => run(false)}
        disabled={selected.length === 0 || isLoading}
        title={config.label}
        confirmName={config.menuConfirmName}
        backName="Cancel"
      >
        <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
          <AddScopeTree
            domains={domains}
            selected={selected}
            onChange={setSelected}
          />
        </Box>
      </ConfirmDialog>

      <NodeActionConfirmModal
        open={open && confirming}
        title={config.menuLabel}
        text={config.menuText}
        toChange={outcomes
          .filter(
            (o) =>
              o.result === NodeActionResult.Updated ||
              o.result === NodeActionResult.Skipped,
          )
          .map(toItem)}
        toChangeTitle={config.menuToChangeTitle}
        skipped={outcomes
          .filter(
            (o) =>
              o.result === NodeActionResult.Ineligible ||
              o.result === NodeActionResult.Blocked,
          )
          .map(toItem)}
        toSkipTitle={config.menuToSkipTitle}
        confirmName={config.menuConfirmName}
        onBack={close}
        onConfirm={() => {
          if (!isLoading) run(true);
        }}
      />
    </>
  );
};
